import React, { useContext, useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";

import {
  Grid,
  Collapse,
  Typography,
  IconButton,
  Table,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
  TablePagination,
  Tooltip,
  Checkbox,
  Select,
  MenuItem,
  LinearProgress,
} from "@material-ui/core";
import { CloudDownload, ExpandLess, ExpandMore, ViewColumn } from "@material-ui/icons";
import { CSVLink } from "react-csv";
import { useTheme } from "@material-ui/core/styles";

import { OtaContext, FleetContext } from "../../context";
import { SectionGridContainer } from "../../components/Layout";
import { KeyValueDisplay } from "../../components/Typography";
import { StringUtils } from "../../util";
import OtaVehiclesBulkDropdown from "./OtaVehiclesBulkDropdown";
import PassiveHardwareModal from "./PassiveHardwareModal";
import SendPackagesModal from "./SendPackagesModal";
import FleetTransferModal from "./FleetTransferModal";
import HibernateVehiclesModal from "./HibernateVehiclesModal";
import NoVehiclesModal from "./NoVehiclesModal";
const OtaVehiclesTable = () => {
  const theme = useTheme();
  const {
    otaVehicles,
    firmwarePackages,
    geofencePackages,
    firmwareTargetPackage,
    geofenceTargetPackage,
    setFirmwareTargetPackage,
    setGeofenceTargetPackage,
    setOtaFilterParams,
    applyGeofencePackage,
    applyFirmwarePackage,
    stopGeofencePackages,
    stopFirmwarePackages,
    installPassiveHardware,
    fleetTransfer,
    hibernateVehicles,
    hibernateVehiclesDetails,
    inProgressFpaCount,
    inProgressGpaCount,
    MAX_FPA_PER_FLEET,
    MAX_GPA_PER_FLEET,
    otaFilterParams,
    fetchOtaVehicles,
  } = useContext(OtaContext);
  const { emptyPatchVehicle, activeFleet } = useContext(FleetContext);
  const [showVehicles, setShowVehicles] = useState(true);
  const [columnFields, setColumnFields] = useState([]);
  const [selectedVehicles, setSelectedVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [maxOtaFirmwareExceeded, setMaxFirmwareOtaExceeded] = useState(false);
  const [maxOtaGeofenceExceeded, setMaxGeofenceOtaExceeded] = useState(false);
  const [isInstallPassiveOpen, setIsInstallPassiveOpen] = useState(false);
  const [isGeofenceModalOpen, setIsGeofenceModalOpen] = useState(false);
  const [isFirmwareModalOpen, setIsFirmwareModalOpen] = useState(false);
  const [isFleetTransferOpen, setIsFleetTransferOpen] = useState(false);
  const [isHibernateVehiclesOpen, setIsHibernateVehiclesOpen] = useState(false);
  const [isNoVehiclesOpen, setIsNoVehiclesOpen] = useState(false);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [csvData, setCsvData] = useState([]);
  const { fleetId } = useParams();
  const rowsPerPageOptions = [10, 30, 50, 100];
  if (!rowsPerPageOptions.includes(otaFilterParams.page_size)) {
    // if we get a rowsPerPage from env that wasn't
    // previously an option, add it, re-sort list
    rowsPerPageOptions.push(otaFilterParams.page_size);
    rowsPerPageOptions.sort((a, b) => a - b);
  }
  const columnFieldOptions = [
    "target_state",
    "last_reported_state",
    "deployment_status",
    "possession_status",
    "provisioning_status",
    "battery_charge",
    "sc_fw_version",
    "md_fw_version",
    "bms_fw_version",
    "lg69t_gnss_version",
    "lg69t_app_version",
    "sc_serial_number",
    "md_serial_number",
    "bms_serial_number",
    "geofence_rules_id",
    "geofence_map_id",
    "created",
    "modified",
    "cell_strength",
    "maintenance",
    "model_number",
    "serial_number",
    "last_ingest",
    "gpa_in_progress",
    "fpa_in_progress",
    "scooter_type",
    "last_reported_network",
    "radio_cert_expiration",
    "sc_imei",
    "fin",
    "operational_speed_limit",
  ];

  const csvRef = useRef(null);

  const selectAll = () => {
    if (otaVehicles && selectedVehicles.length < otaVehicles.results?.length)
      setSelectedVehicles(otaVehicles.results.map((r) => r.id));
    else setSelectedVehicles([]);
  };

  const selectVehicle = (id) => {
    if (selectedVehicles.includes(id))
      setSelectedVehicles(selectedVehicles.filter((entry) => id !== entry));
    else setSelectedVehicles([...selectedVehicles, id]);
  };

  const onSelectBulkDropdown = (event) => {
    const selected = event.target.getAttribute("name");
    if (selectedVehicles.length === 0) {
      switch (selected) {
        case "Hibernate Vehicles":
          setIsHibernateVehiclesOpen(true);
          break;
        default:
          setIsNoVehiclesOpen(true);
      }
    } else {
      switch (selected) {
        case "Install Passive Hardware":
          setIsInstallPassiveOpen(true);
          break;
        case "Poke At Selected":
          pokeVehicles();
          break;
        case "Stop Geofence Packages":
          stopGeofence();
          break;
        case "Stop Firmware Packages":
          stopFirmware();
          break;
        case "Send Geofence Packages":
          setIsGeofenceModalOpen(true);
          break;
        case "Send Firmware Packages":
          setIsFirmwareModalOpen(true);
          break;
        case "Fleet Transfer":
          setIsFleetTransferOpen(true);
          break;
        case "Hibernate Vehicles":
          setIsHibernateVehiclesOpen(true);
          break;
        default:
          break;
      }
    }
  };

  const closePassiveHardwareModal = () => {
    setIsInstallPassiveOpen(false);
  };

  const closeGeofenceModal = () => {
    setIsGeofenceModalOpen(false);
  };

  const closeFirmwareModal = () => {
    setIsFirmwareModalOpen(false);
  };

  const closeFleetModal = () => {
    setIsFleetTransferOpen(false);
  };

  const closeHibernateModal = () => {
    setIsHibernateVehiclesOpen(false);
  };

  const closeNoVehicles = () => {
    setIsNoVehiclesOpen(false);
  };

  const applyGeofence = async () => {
    setLoading(true);
    await applyGeofencePackage(selectedVehicles, geofenceTargetPackage);
    if (activeFleet?.default_geofence_package) {
      setGeofenceTargetPackage(activeFleet.default_geofence_package);
    }
    setLoading(false);
  };

  const applyFirmware = async () => {
    setLoading(true);
    await applyFirmwarePackage(selectedVehicles, StringUtils.uuidFromUrl(firmwareTargetPackage));
    if (activeFleet?.default_firmware_package) {
      setFirmwareTargetPackage(activeFleet.default_firmware_package);
    }
    setLoading(false);
  };

  const stopGeofence = async () => {
    setLoading(true);
    await stopGeofencePackages(selectedVehicles);
    setLoading(false);
  };

  const stopFirmware = async () => {
    setLoading(true);
    await stopFirmwarePackages(selectedVehicles);
    setLoading(false);
  };

  const pokeVehicles = async (vehicleIds) => {
    setLoading(true);
    const ids = vehicleIds ?? selectedVehicles;
    await Promise.all(ids.map((id) => emptyPatchVehicle(id)));
    setLoading(false);
  };

  const loadCsv = async () => {
    setLoading(true);
    let page = 1;
    let res = await fetchOtaVehicles(otaFilterParams, true, page);
    let vehicles = res?.results;
    while (res?.next) {
      page += 1;
      // eslint-disable-next-line no-await-in-loop
      res = await fetchOtaVehicles(otaFilterParams, true, page);
      vehicles = vehicles.concat(res?.results);
    }
    const data = vehicles.map((v) => {
      const vehicleObject = {};
      vehicleObject.id = v.id;
      vehicleObject.shortcode = String(v.shortcodes[0]?.shortcode);
      columnFields.forEach((col) => {
        if (col === "gpa_in_progress") {
          vehicleObject[col] = String(!!v.gpa_in_progress);
        } else if (col === "fpa_in_progress") {
          vehicleObject[col] = String(!!v.fpa_in_progress);
        } else if (v[col] instanceof Date) {
          vehicleObject[col] = v[col].toLocaleString();
        } else {
          vehicleObject[col] = v[col];
        }
      });
      return vehicleObject;
    });
    setCsvData([...data]);
    csvRef.current.link.click();
    setLoading(false);
  };

  useEffect(() => {
    if (localStorage.getItem(`${fleetId}-expandVehicles`) === null) setShowVehicles(true);
    else setShowVehicles(JSON.parse(localStorage.getItem(`${fleetId}-expandVehicles`)));

    if (localStorage.getItem(`${fleetId}-columnFields`) === null) setColumnFields([]);
    else setColumnFields(JSON.parse(localStorage.getItem(`${fleetId}-columnFields`)));
  }, []);

  useEffect(() => {
    localStorage.setItem(`${fleetId}-expandVehicles`, JSON.stringify(showVehicles));
  }, [showVehicles, fleetId]);

  useEffect(() => {
    if (activeFleet?.default_firmware_package) {
      setFirmwareTargetPackage(activeFleet.default_firmware_package);
    }
    if (activeFleet?.default_geofence_package) {
      setGeofenceTargetPackage(activeFleet.default_geofence_package);
    }
  }, [activeFleet]);

  useEffect(() => {
    localStorage.setItem(`${fleetId}-columnFields`, JSON.stringify(columnFields));
  }, [columnFields, fleetId]);

  useEffect(() => {
    setSelectedVehicles([]);
  }, [otaVehicles]);

  useEffect(() => {
    const length = selectedVehicles.length || 1;
    const fpaCount =
      inProgressFpaCount === Object(inProgressFpaCount) &&
      inProgressFpaCount[fleetId] &&
      !Number.isNaN(inProgressFpaCount[fleetId])
        ? inProgressFpaCount[fleetId] + length
        : length;

    const gpaCount =
      inProgressGpaCount === Object(inProgressGpaCount) &&
      inProgressGpaCount[fleetId] &&
      !Number.isNaN(inProgressGpaCount[fleetId])
        ? inProgressGpaCount[fleetId] + length
        : length;
    const overMaxFpa = fpaCount > MAX_FPA_PER_FLEET;
    const overMaxGpa = gpaCount > MAX_GPA_PER_FLEET;

    setMaxFirmwareOtaExceeded(overMaxFpa);
    setMaxGeofenceOtaExceeded(overMaxGpa);
  }, [inProgressFpaCount, fleetId, selectedVehicles]);

  return (
    <SectionGridContainer
      spaceContents
      style={{ padding: theme.spacing(1, 2, 1, 2) }}
      key={fleetId}
    >
      <Grid item container sm={12} alignItems="center">
        <Grid item sm={4}>
          <Typography variant="h5">
            {selectedVehicles.length < 1
              ? `${otaVehicles?.count || 0} Vehicles`
              : `${selectedVehicles.length} row(s) selected of ${otaVehicles?.results.length || 0}`}
          </Typography>
        </Grid>
        <Grid item container sm={8} justify="flex-end">
          <Grid item>
            <IconButton size="small" onClick={() => setShowVehicles(!showVehicles)}>
              {showVehicles ? <ExpandLess fontSize="large" /> : <ExpandMore fontSize="large" />}
            </IconButton>
          </Grid>
        </Grid>
      </Grid>
      <Collapse
        in={showVehicles}
        style={!showVehicles ? { paddingBottom: "0px", width: "100%" } : { width: "100%" }}
      >
        <Grid item container sm={12} spacing={1}>
          <Grid item container sm={12} spacing={1} alignItems="center">
            <Grid item style={{ marginLeft: "auto" }}>
              <Tooltip title="Download CSV">
                <IconButton onClick={loadCsv}>
                  <CloudDownload />
                </IconButton>
              </Tooltip>
              <CSVLink
                data={csvData}
                filename="filtered_vehicles.csv"
                style={{ display: "none" }}
                ref={csvRef}
              />
              <Tooltip title="View Columns">
                <IconButton
                  style={{ marginRight: 12 }}
                  onClick={() => setColumnsOpen(true)}
                  id="open-columns"
                >
                  <ViewColumn />
                </IconButton>
              </Tooltip>
              <Select
                value={columnFields}
                multiple
                onChange={(e) => {
                  setColumnFields(e.target.value);
                }}
                open={columnsOpen}
                style={{ display: "none" }}
                onClose={() => setColumnsOpen(false)}
                MenuProps={{
                  anchorEl: document.getElementById("open-columns"),
                }}
              >
                {columnFieldOptions.map((col) => (
                  <MenuItem key={col} value={col}>
                    {col}
                  </MenuItem>
                ))}
              </Select>
              <OtaVehiclesBulkDropdown
                onSelect={onSelectBulkDropdown}
                overMaxFpa={maxOtaFirmwareExceeded}
                overMaxGpa={maxOtaGeofenceExceeded}
              />
            </Grid>
          </Grid>
          <PassiveHardwareModal
            modalOpen={isInstallPassiveOpen}
            handleModalClose={closePassiveHardwareModal}
            selectedVehicles={selectedVehicles}
            installPassiveHardware={installPassiveHardware}
            setLoading={setLoading}
          />
          <SendPackagesModal
            modalOpen={isGeofenceModalOpen}
            handleModalClose={closeGeofenceModal}
            selectedVehicles={selectedVehicles}
            setLoading={setLoading}
            type="Geofence"
            geofencePackages={geofencePackages.filter(
              (gfp) => !gfp.version.toLowerCase().includes("preview"),
            )}
            defaultGeofence={activeFleet?.default_geofence_package || ""}
            applyPackages={applyGeofence}
          />
          <SendPackagesModal
            modalOpen={isFirmwareModalOpen}
            handleModalClose={closeFirmwareModal}
            selectedVehicles={selectedVehicles}
            setLoading={setLoading}
            type="Firmware"
            firmwarePackages={firmwarePackages}
            defaultFirmware={activeFleet?.default_firmware_package || ""}
            applyPackages={applyFirmware}
          />
          <FleetTransferModal
            modalOpen={isFleetTransferOpen}
            handleModalClose={closeFleetModal}
            selectedVehicles={selectedVehicles}
            fleetTransfer={fleetTransfer}
            setLoading={setLoading}
          />
          <HibernateVehiclesModal
            modalOpen={isHibernateVehiclesOpen}
            handleModalClose={closeHibernateModal}
            hibernateVehicles={hibernateVehicles}
            hibernateVehiclesDetails={hibernateVehiclesDetails}
            setLoading={setLoading}
            activeFleet={activeFleet}
          />
          <NoVehiclesModal modalOpen={isNoVehiclesOpen} handleModalClose={closeNoVehicles} />
          {loading && (
            <Grid item xs={12} style={{ padding: "0px" }}>
              <LinearProgress color="secondary" />
            </Grid>
          )}
          <div style={{ width: "100%", maxHeight: 400, overflow: "auto" }}>
            <Table>
              <TableHead style={{ position: "sticky", top: 0, zIndex: 2, background: "#303749" }}>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      aria-label="select-all-vehicles"
                      onChange={selectAll}
                      checked={
                        !!otaVehicles && selectedVehicles.length === otaVehicles.results?.length
                      }
                    />
                  </TableCell>
                  <TableCell>Vehicle</TableCell>
                  {columnFields.map((col) => (
                    <TableCell key={col}>{col}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody style={{ zIndex: 1 }}>
                {otaVehicles?.results.map((v) => (
                  <TableRow key={v.id}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedVehicles.includes(v.id)}
                        onChange={() => selectVehicle(v.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <KeyValueDisplay
                        label={String(v.shortcodes[0]?.shortcode)}
                        labelColor="textPrimary"
                        text={v.id}
                        textColor="textPrimary"
                        separator=""
                        maxTextWidth="7rem"
                        copyToClipboard
                        labelLinkTo={`/fleets/${fleetId}/vehicles/${v.id}`}
                      />
                    </TableCell>
                    {columnFields.map((col) => {
                      if (col === "gpa_in_progress")
                        return <TableCell key={col}>{String(!!v.gpa_in_progress)}</TableCell>;
                      if (col === "fpa_in_progress")
                        return <TableCell key={col}>{String(!!v.fpa_in_progress)}</TableCell>;
                      if (v[col] instanceof Date)
                        return <TableCell key={col}>{v[col].toLocaleString()}</TableCell>;
                      return <TableCell key={col}>{v[col]}</TableCell>;
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <Table>
            <TableBody>
              <TableRow>
                <TablePagination
                  count={otaVehicles?.count ?? 0}
                  page={otaVehicles?.current ? otaVehicles.current - 1 : 0}
                  rowsPerPage={Number(otaFilterParams.page_size)}
                  onChangeRowsPerPage={(e) => setOtaFilterParams({ page_size: e.target.value })}
                  rowsPerPageOptions={rowsPerPageOptions}
                  backIconButtonProps={{
                    "aria-label": "Previous Page",
                  }}
                  nextIconButtonProps={{
                    "aria-label": "Next Page",
                  }}
                  style={{ border: "none", marginLeft: "auto" }}
                  onChangePage={(e, page) =>
                    fetchOtaVehicles({ ...otaFilterParams, page: page + 1 })
                  }
                />
              </TableRow>
            </TableBody>
          </Table>
        </Grid>
      </Collapse>
    </SectionGridContainer>
  );
};

export default OtaVehiclesTable;
